import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DarkMode, UserModel } from 'app/account-settings/models/user.model';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { BehaviorSubject } from 'rxjs';
import { UserService } from '../../user/user.service';
import { AdminSettings, AppTheme } from './admin-settings.model';
import { SettingsService } from './admin-settings.service';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  // Read by the pre-bootstrap seed in index.html; keep key + DarkMode value in sync.
  private static readonly colorSchemeStorageKey = 'tb-color-scheme';
  // Read by the pre-bootstrap seed in index.html; keep key + AppTheme value in sync.
  private static readonly appThemeStorageKey = 'tb-app-theme';
  // Body classes per theme. Both 2026 variants share the theme-2026 base (which carries the font,
  // grayscale controls, chart palette, etc.); a variant class carries the differences.
  // 2025 is the one theme with no class.
  private static readonly theme2026BaseClass = 'theme-2026';
  private static readonly theme2026GoldenClass = 'theme-2026-golden';
  private static readonly theme2026BlueGoldClass = 'theme-2026-blue-gold';

  private readonly logger: Logger;
  public darkModeMediaQuery: any;
  public toggleThemeBind: any;
  public isDarkThemeBS = new BehaviorSubject<boolean>(undefined);
  private settingsSubscription: Subscription;
  private userSubscription: Subscription;
  public adminTheme: DarkMode = null;
  public userTheme: DarkMode;
  private readonly destroyRef = inject(DestroyRef);
  constructor(
    public loggerFactory: LoggerFactory,
    private readonly userService: UserService,
    private readonly settingsService: SettingsService,
  ) {}

  public init(user?: UserModel) {
    if (user) {
      this.userTheme = user.darkMode;
      this.changeTheme(user.darkMode);
    } else {
      // Tear down previous subscription to avoid stacking listeners on re-init.
      this.userSubscription?.unsubscribe();
      this.userSubscription = this.userService
        .getUserAsObservable()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((user: UserModel) => {
          if (user) {
            this.userTheme = user.darkMode;
            this.changeTheme(user.darkMode);
          }
        });
    }
    this.settingsSubscription?.unsubscribe();
    this.settingsSubscription = this.settingsService.settings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings: AdminSettings) => {
        this.adminTheme = settings.adminTheme;
        this.changeTheme(this.userTheme);
        this.applyAppTheme(settings.appTheme);
      });
  }

  public applyAppTheme(appTheme: AppTheme): void {
    // Resolved once so the body classes and the persisted key cannot disagree.
    const theme = appTheme ?? AppTheme.Theme2026BlueGold;
    const isGolden = theme === AppTheme.Theme2026Golden;
    const isBlueGold = theme === AppTheme.Theme2026BlueGold;
    const classes = document.body.classList;
    classes.toggle(ThemeService.theme2026BaseClass, isGolden || isBlueGold);
    classes.toggle(ThemeService.theme2026GoldenClass, isGolden);
    classes.toggle(ThemeService.theme2026BlueGoldClass, isBlueGold);
    try {
      localStorage.setItem(ThemeService.appThemeStorageKey, theme);
    } catch {
      // best-effort; storage may be unavailable
    }
  }

  public changeTheme(darkMode: DarkMode) {
    if (!darkMode) {
      // No explicit preference saved yet (e.g. a newly registered user) — follow the browser.
      darkMode = DarkMode.DeviceSettingsBased;
    } else {
      this.persistColorScheme(darkMode);
    }
    if (this.adminTheme !== null) {
      darkMode = this.adminTheme;
    }
    switch (darkMode) {
      case DarkMode.DeviceSettingsBased:
        this.addListenningChangesMode();
        break;
      case DarkMode.AlwaysDark:
        this.toggleTheme(true);
        this.removeListenningChangesMode();
        break;
      case DarkMode.AlwaysLight:
        this.toggleTheme(false);
        this.removeListenningChangesMode();
        break;
      default:
        this.toggleTheme(false);
        this.removeListenningChangesMode();
        break;
    }
  }

  private persistColorScheme(darkMode: DarkMode): void {
    try {
      localStorage.setItem(ThemeService.colorSchemeStorageKey, darkMode);
    } catch {
      // best-effort; storage may be unavailable
    }
  }

  public toggleTheme(e): void {
    e ? document.body.classList.add('dark-mode') : document.body.classList.remove('dark-mode');
    this.isDarkThemeBS ? this.isDarkThemeBS.next(e) : null;
  }

  public toggleThemeListener(isDarkThemeBS, e): void {
    e.matches ? document.body.classList.add('dark-mode') : document.body.classList.remove('dark-mode');
    isDarkThemeBS ? isDarkThemeBS.next(e.matches) : null;
  }

  public addListenningChangesMode() {
    this.darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.toggleTheme(this.darkModeMediaQuery.matches);
    this.toggleThemeBind = this.toggleThemeListener.bind(null, this.isDarkThemeBS);
    this.darkModeMediaQuery.addEventListener('change', this.toggleThemeBind);
  }

  public removeListenningChangesMode() {
    if (this.darkModeMediaQuery) {
      this.darkModeMediaQuery.removeEventListener('change', this.toggleThemeBind);
    }
  }
}
